///
/// EarthBrowser
///
/// Created by Matt Giger on 9/18/2026
/// Copyright (c) 2026 Geodata Labs
///
/// Drop-pin gesture: long-press (touch) or right-click (pointer) on the
/// globe opens the drop-pin context menu at the hit point, from which the
/// user can create a placemark or add the location to an existing one.
/// The menu itself is React (DropPinMenu), driven through the ephemeral
/// dialog slice; this class only detects the gesture and resolves the
/// geographic hit.
///

import { type EarthBrowserContext } from "@/earthbrowser/EarthBrowserContext"
import { engineActions, engineBridge } from "@/earthbrowser/engineBridge"
import { geographic } from "@/earthbrowser/utils/geographic"
import { GestureState } from "@stovokor/gestures/GestureRecognizer"
import { LongPressGestureRecognizer } from "@stovokor/gestures/LongPressGestureRecognizer"
import { trig } from "@stovokor/math"
import { type vec3 } from "@stovokor/math/vector"
import { type Earth } from "../Earth"
import { type EarthCameraState } from "../EarthCameraState"

export class EarthDropPinHandler {
	private longPressHandler: LongPressGestureRecognizer
	private contextMenuListener: (event: MouseEvent) => void

	constructor(context: EarthBrowserContext, earth: Earth, cameraState: EarthCameraState) {
		// Touch: press and hold on the globe (recognition fails on drag)
		this.longPressHandler = new LongPressGestureRecognizer({ maximumTouches: 1 })
		earth.addRecognizer(this.longPressHandler)
		this.longPressHandler.onGesture(data => {
			if (data.state !== GestureState.Began) return
			const hit = data.ownerHits[0]?.hitPoint
			if (hit) {
				this.openMenu(data.x, data.y, hit)
			}
		})

		// Pointer: right-click. The gesture system has no contextmenu path,
		// so listen on the canvas directly and raycast the screen point.
		this.contextMenuListener = (event: MouseEvent) => {
			const rect = context.canvas.getBoundingClientRect()
			const x = event.clientX - rect.left
			const y = event.clientY - rect.top
			const hit = cameraState.hitTestFromScreen(x, y)
			if (hit) {
				event.preventDefault()
				this.openMenu(x, y, hit)
			}
		}
		context.canvas.addEventListener("contextmenu", this.contextMenuListener)
	}

	private openMenu(x: number, y: number, hitPoint: vec3): void {
		const geo = new geographic(hitPoint)
		engineBridge.dispatch({
			type: engineActions.setDropPinMenu,
			payload: {
				x,
				y,
				lat: geo.latitude * trig.deg,
				lon: geo.longitude * trig.deg,
			}
		})
	}
}
